import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Output,
} from '@angular/core';

@Component({
    selector: 'app-custom-datetime-picker',
    templateUrl: './custom-datetime-picker.component.html',
    styleUrls: ['./custom-datetime-picker.component.scss'],
})
export class CustomDatetimePickerComponent {
    @Output() dateTimeSelected = new EventEmitter<Date>();

    popupVisible = true; // Keep popup open initially
    selectedDate: Date | null = null;
    selectedHour: number | null = null;
    selectedMinute: number | null = null;

    displayValue = '';
    currentCalendarDate = new Date();

    selectedMonth: number;
    selectedYear: number;
    yearOptions: number[] = [];

    monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    calendarDays: (number | null)[][] = [];

    // For keyboard navigation focus tracking
    focusedDayIndex: { week: number; day: number } | null = null;
    focusedHour: number | null = null;
    focusedMinute: number | null = null;

    constructor(private eRef: ElementRef) {
        const now = new Date();
        this.selectedMonth = now.getMonth();
        this.selectedYear = now.getFullYear();

        for (let y = this.selectedYear; y <= this.selectedYear + 5; y++) {
            this.yearOptions.push(y);
        }

        this.generateCalendar(this.currentCalendarDate);
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: MouseEvent) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            // If click outside, close popup only if a datetime is selected
            if (
                this.selectedDate !== null &&
                this.selectedHour !== null &&
                this.selectedMinute !== null
            ) {
                this.popupVisible = false;
            }
        }
    }

    @HostListener('document:keydown', ['$event'])
    handleArrowKey(event: KeyboardEvent) {
        if (!this.popupVisible) return;

        if (event.key === 'Backspace') {
            event.preventDefault();
            if (this.isSelectingMinute()) {
                this.goToDateSelection();
            } else if (this.isSelectingHour()) {
                this.goToDateSelection();
            }
            return;
        }

        if (this.isSelectingDate()) {
            this.handleDateArrowNavigation(event);
        } else if (this.isSelectingHour()) {
            this.handleHourArrowNavigation(event);
        } else if (this.isSelectingMinute()) {
            this.handleMinuteArrowNavigation(event);
        }
    }

    // ----- Date navigation with arrows and Enter -----
    handleDateArrowNavigation(event: KeyboardEvent) {
        event.preventDefault();

        if (!this.focusedDayIndex) {
            // Focus first valid day (not past)
            outer: for (let w = 0; w < this.calendarDays.length; w++) {
                for (let d = 0; d < 7; d++) {
                    const day = this.calendarDays[w][d];
                    if (day !== null && !this.isPastDate(day)) {
                        this.focusedDayIndex = { week: w, day: d };
                        return;
                    }
                }
            }
        } else {
            let { week, day } = this.focusedDayIndex;

            switch (event.key) {
                case 'ArrowRight':
                    day++;
                    if (day > 6) {
                        day = 0;
                        week++;
                    }
                    if (
                        week >= this.calendarDays.length ||
                        this.calendarDays[week][day] === null
                    ) {
                        this.incrementMonth();
                        return;
                    }
                    break;

                case 'ArrowLeft':
                    day--;
                    if (day < 0) {
                        day = 6;
                        week--;
                    }
                    if (week < 0 || this.calendarDays[week][day] === null) {
                        this.decrementMonth();
                        return;
                    }
                    break;

                case 'ArrowDown':
                    week++;
                    if (
                        week >= this.calendarDays.length ||
                        this.calendarDays[week][day] === null
                    )
                        return;
                    break;

                case 'ArrowUp':
                    week--;
                    if (week < 0 || this.calendarDays[week][day] === null)
                        return;
                    break;

                case 'Enter':
                    const selectedDay = this.calendarDays[week][day];
                    if (selectedDay !== null && !this.isPastDate(selectedDay)) {
                        this.selectDate(selectedDay);
                    }
                    return;
            }

            // Clamp within valid bounds
            if (week < 0) week = 0;
            if (week >= this.calendarDays.length)
                week = this.calendarDays.length - 1;
            if (day < 0) day = 0;
            if (day > 6) day = 6;

            if (
                this.calendarDays[week][day] === null ||
                this.isPastDate(this.calendarDays[week][day]!)
            ) {
                return;
            }

            this.focusedDayIndex = { week, day };
        }
    }

    incrementMonth() {
        if (this.selectedMonth === 11) {
            this.selectedMonth = 0;
            this.selectedYear++;
            this.yearOptions.push(this.selectedYear);
        } else {
            this.selectedMonth++;
        }
        this.onMonthChange();
    }

    decrementMonth() {
        const now = new Date();
        if (
            this.selectedYear === now.getFullYear() &&
            this.selectedMonth === now.getMonth()
        ) {
            return; // Prevent going to past month
        }

        if (this.selectedMonth === 0) {
            this.selectedMonth = 11;
            this.selectedYear--;
        } else {
            this.selectedMonth--;
        }
        this.onMonthChange();
    }

    // ----- Hour navigation -----
    handleHourArrowNavigation(event: KeyboardEvent) {
        event.preventDefault();

        if (this.focusedHour === null) {
            this.focusedHour = 0;
        } else {
            switch (event.key) {
                case 'ArrowRight':
                    this.focusedHour = (this.focusedHour + 1) % 24;
                    break;
                case 'ArrowLeft':
                    this.focusedHour = (this.focusedHour - 1 + 24) % 24;
                    break;
                case 'ArrowDown':
                    this.focusedHour = Math.min(this.focusedHour + 5, 23);
                    break;
                case 'ArrowUp':
                    this.focusedHour = Math.max(this.focusedHour - 5, 0);
                    break;
                case 'Enter':
                    if (!this.isPastHour(this.focusedHour)) {
                        this.selectHour(this.focusedHour);
                    }
                    return;
            }
        }
    }

    // ----- Minute navigation -----
    handleMinuteArrowNavigation(event: KeyboardEvent) {
        event.preventDefault();

        const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
        if (this.focusedMinute === null) {
            this.focusedMinute = 0;
        } else {
            const currentIndex = minutes.indexOf(this.focusedMinute);
            switch (event.key) {
                case 'ArrowRight':
                    this.focusedMinute =
                        minutes[(currentIndex + 1) % minutes.length];
                    break;
                case 'ArrowLeft':
                    this.focusedMinute =
                        minutes[
                            (currentIndex - 1 + minutes.length) % minutes.length
                        ];
                    break;
                case 'ArrowDown':
                    this.focusedMinute =
                        currentIndex + 5 < minutes.length
                            ? minutes[currentIndex + 5]
                            : this.focusedMinute;
                    break;
                case 'ArrowUp':
                    this.focusedMinute =
                        currentIndex - 5 >= 0
                            ? minutes[currentIndex - 5]
                            : this.focusedMinute;
                    break;
                case 'Enter':
                    if (!this.isPastMinute(this.focusedMinute)) {
                        this.selectMinute(this.focusedMinute);
                    }
                    return;
            }
        }
    }

    // ----- Calendar generation -----
    generateCalendar(date: Date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const firstWeekDay = firstDay.getDay(); // Sunday=0...Saturday=6
        const daysInMonth = lastDay.getDate();

        this.calendarDays = [];
        let dayCounter = 1;

        for (let week = 0; week < 6; week++) {
            const weekDays: (number | null)[] = [];
            for (let day = 0; day < 7; day++) {
                const cellIndex = week * 7 + day;
                if (cellIndex >= firstWeekDay && dayCounter <= daysInMonth) {
                    weekDays.push(dayCounter++);
                } else {
                    weekDays.push(null);
                }
            }
            this.calendarDays.push(weekDays);
        }
    }

    onMonthChange() {
        const now = new Date();
        if (
            this.selectedYear === now.getFullYear() &&
            this.selectedMonth < now.getMonth()
        ) {
            this.selectedMonth = now.getMonth();
        }
        this.currentCalendarDate = new Date(
            this.selectedYear,
            this.selectedMonth,
            1
        );
        this.generateCalendar(this.currentCalendarDate);
        this.focusedDayIndex = null; // Reset focus on month change
    }

    onYearChange() {
        const now = new Date();
        if (this.selectedYear === now.getFullYear()) {
            if (this.selectedMonth < now.getMonth()) {
                this.selectedMonth = now.getMonth();
            }
        } else {
            this.selectedMonth = 0;
        }
        this.onMonthChange();
    }

    isMonthDisabled(year: number, month: number): boolean {
        const now = new Date();
        return year === now.getFullYear() && month < now.getMonth();
    }

    selectDate(day: number) {
        if (this.isPastDate(day)) return;

        const year = this.currentCalendarDate.getFullYear();
        const month = this.currentCalendarDate.getMonth();
        this.selectedDate = new Date(year, month, day);

        const now = new Date();
        this.focusedHour = now.getHours();
        this.focusedMinute = this.getClosestMinute(now.getMinutes());

        this.selectedHour = null;
        this.selectedMinute = null;
    }

    selectHour(hour: number) {
        if (this.isPastHour(hour)) return;

        this.selectedHour = hour;

        const now = new Date();
        this.focusedMinute = this.getClosestMinute(now.getMinutes());

        this.selectedMinute = null;
    }

    selectMinute(minute: number) {
        if (this.isPastMinute(minute)) return;
        this.selectedMinute = minute;
        this.setValueAndClose();
    }

    setValueAndClose() {
        if (
            !this.selectedDate ||
            this.selectedHour === null ||
            this.selectedMinute === null
        ) {
            return;
        }

        const finalDateTime = new Date(this.selectedDate);
        finalDateTime.setHours(this.selectedHour);
        finalDateTime.setMinutes(this.selectedMinute);
        finalDateTime.setSeconds(0);
        finalDateTime.setMilliseconds(0);

        this.dateTimeSelected.emit(finalDateTime);

        // Format display string
        const yyyy = finalDateTime.getFullYear();
        const mm = (finalDateTime.getMonth() + 1).toString().padStart(2, '0');
        const dd = finalDateTime.getDate().toString().padStart(2, '0');
        const hh = finalDateTime.getHours().toString().padStart(2, '0');
        const min = finalDateTime.getMinutes().toString().padStart(2, '0');

        this.displayValue = `${yyyy}-${mm}-${dd}T${hh}:${min}`;
        this.popupVisible = false; // Close popup after complete selection
    }

    isSelectingDate(): boolean {
        return this.selectedDate === null;
    }

    isSelectingHour(): boolean {
        return this.selectedDate !== null && this.selectedHour === null;
    }

    isSelectingMinute(): boolean {
        return this.selectedHour !== null && this.selectedMinute === null;
    }

    isPastDate(day: number): boolean {
        const today = new Date();
        const dateToCheck = new Date(
            this.currentCalendarDate.getFullYear(),
            this.currentCalendarDate.getMonth(),
            day
        );
        today.setHours(0, 0, 0, 0);
        dateToCheck.setHours(0, 0, 0, 0);
        return dateToCheck < today;
    }

    isPastHour(hour: number): boolean {
        if (!this.selectedDate) return false;
        const now = new Date();
        if (this.selectedDate.toDateString() !== now.toDateString())
            return false;
        return hour < now.getHours();
    }

    isPastMinute(minute: number): boolean {
        if (!this.selectedDate || this.selectedHour === null) return false;
        const now = new Date();
        if (this.selectedDate.toDateString() !== now.toDateString())
            return false;
        if (this.selectedHour !== now.getHours()) return false;
        return minute < now.getMinutes();
    }

    getClosestMinute(minute: number): number {
        const steps = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
        return steps.reduce((prev, curr) =>
            Math.abs(curr - minute) < Math.abs(prev - minute) ? curr : prev
        );
    }

    // Back button navigation
    goToDateSelection() {
        this.selectedDate = null;
        this.selectedHour = null;
        this.selectedMinute = null;
        this.focusedDayIndex = null;
        this.focusedHour = null;
        this.focusedMinute = null;
        this.popupVisible = true;
    }
}
